import { createContext, useContext } from "react";
import { SupplierColumns, useSupplierColumns } from "./useSupplierColumns";
import { useGridColumns, GridColumnsType } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { CompanyGrid } from "src/api/client-api/company/useGetAllOrganization";
import { useGetOrganizationSuppliers } from "src/api/client-api/company/useGetOrganizationSuppliers";
import { DelayedSearch, useDelayedSearch } from "src/utils/useDelayedSearch";
import { useUrlToApi } from "src/api/_common/params/useUrlToApi";
import { AppSearch } from "src/api/_common/hydra/search";
import { useSuppliersGridMenuItems } from "./SuppliersGrid/useSuppliersGridMenuItems";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { MenuItemType } from "src/components/ActionsPopover/ActionsPopover.types";
import { ApiPagination } from "src/api/_common/params/useApiPaginationUrlSync";
import type { ComponentAppearance } from "src/components/appearance";

type SupplierListContextInitialValues = {
    gridKey: string;
    mrt_columns: SupplierColumns;
    gridConfiguration: GridColumnsType<CompanyGrid>;
    suppliers: CompanyGrid[] | undefined;
    menuItems: MenuItemType<CompanyGrid>[];
    totalItems: number;
    isRefetching: boolean;
    isReady: boolean;
    appSearch: AppSearch | undefined;
    globalSearch: DelayedSearch;
    pagination: ApiPagination;
};
const SupplierListContext = createContext<SupplierListContextInitialValues | null>(null);

type SupplierListContextProviderProps = { appearance?: ComponentAppearance; children: ReactNode };
export const SupplierListContextProvider = (props: SupplierListContextProviderProps) => {
    const { appearance = "legacy", children } = props;

    const gridKey = "supplier-grid";
    const { company } = useCompanyMetadata();

    const globalSearch = useDelayedSearch();
    const { allAsAxiosParams, pagination } = useUrlToApi({ page: "1", itemsPerPage: "20" });
    const {
        organizations: suppliers,
        isRefetching,
        isReady,
        totalItems,
        appSearch,
    } = useGetOrganizationSuppliers(company.id, {
        search: globalSearch.delayedSearch,
        additionalParams: allAsAxiosParams,
    });

    const mrt_supplierColumns = useSupplierColumns(suppliers);
    const gridConfiguration = useGridColumns(mrt_supplierColumns, gridKey);

    const menuItems = useSuppliersGridMenuItems({
        appearance,
        companyIds: {
            "@id": company["@id"],
            id: company.id,
        },
    });

    const items: SupplierListContextInitialValues = {
        mrt_columns: mrt_supplierColumns,
        menuItems,
        gridConfiguration,
        totalItems,
        isReady,
        isRefetching,
        suppliers: suppliers ?? [],
        globalSearch,
        gridKey: gridKey,
        appSearch,
        pagination,
    };

    return <SupplierListContext.Provider value={items}>{children}</SupplierListContext.Provider>;
};

export const useSupplierListContext = () => {
    const context = useContext(SupplierListContext);

    if (context) {
        return context;
    }

    throw new Error(`useSupplierListContext must be used within an SupplierListContextProvider`);
};
